import React, { useEffect, useState } from 'react';
import pathObj from '../../services/Config';
import MyAccountSideBar from '../../components/MyAccount/MyAccountSideBar';
import { CommonService } from '../../services/common';
import { checkImage, decryptData, errorHandling, UpdatedDecimalDigits } from '../../helpers/common';
import toast from 'react-hot-toast';
import debounce from 'lodash.debounce';
import moment from 'moment'; // For date formatting
import getSymbolFromCurrency from 'currency-symbol-map';
import Pagination from 'react-js-pagination';

const OrderHistory = () => {
  const userInfo = window.localStorage.getItem('userData');
  const userD = decryptData(userInfo);
  const compData = JSON.parse(userD);
  const customerId = compData?.customer_id;
  const currencyName = compData?.Currency
  const currencySymbol = getSymbolFromCurrency(currencyName);
  const [orderedData, setOrderedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null); // State for the selected order
  const [imageStatus, setImageStatus] = useState({});
  const [orderedFilterData, setOrderedFilterData] = useState([]);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [selectedOrderDate, setSelectedOrderDate] = useState('');
  const [isPicklistModalOpen, setIsPicklistModalOpen] = useState(false);
  const [selectedPicklist, setSelectedPicklist] = useState([]);

  const fetchOrderedHistory = async () => {
    setLoading(true);
    const url = `${pathObj.baseURL}${pathObj.getOpenOrders}`; // Update with your API endpoint
    const payload = {
      customer_id: customerId,
      order_date: selectedOrderDate,
      order_status: selectedOrderStatus,
      date_range: dateRange,
    };
  
    try {
      const response = await CommonService.getAllWithBodyWithToken(url, payload);
      if (response.success) {
        setOrderedData(response?.data || []);  // Set data if response is successful
        setOrderedFilterData(response?.orderStatusArr || []);
      } else {
        setOrderedData([]); // Clear the data if the response is unsuccessful
        setErrorText(response.message);
        toast.error(response.message);
      }
    } catch (error) {
      setOrderedData([]); // Clear the data if there's an error
      setErrorText(error?.response?.data?.message || 'An error occurred');
      errorHandling(error);
    } finally {
      setLoading(false);
    }
  };
  

  const fetchOrderItems = async (invoiceNumber) => {
    // setLoading(true);
    const url = `${pathObj.baseURL}${pathObj.getOrderItems}`; // Update with your API endpoint
    const payload = { DocEntry: invoiceNumber, };

    try {
      const response = await CommonService.getAllWithBodyWithToken(url, payload);
      if (response.success) {
        setOrderItems(response?.data || []); // Set fetched order items
        setLoading(false);
        setIsModalOpen(true); // Open modal once order items are fetched
      } else {
        toast.error(response.message);
        setLoading(false);
      }
    } catch (error) {
      setErrorText(error?.response?.data?.message || 'An error occurred');
      setLoading(false);
      errorHandling(error);
    }
  };

  const fetchPicklistData = async (pickListId) => {
    try {
      // Replace with your actual API endpoint and payload
      const url = `${pathObj.baseURL}${pathObj.getPickLists}`;
      const payload = { pick_list_id: pickListId };
      const response = await CommonService.getAllWithBodyWithToken(url, payload);
      if (response.success) {
        setSelectedPicklist(response.data || []);
        setIsPicklistModalOpen(true);
      } else {
        toast.error(response.message || "Failed to fetch picklist data");
      }
    } catch (error) {
      toast.error("Error fetching picklist data");
    }
  };

  const debouncedFetchOrderedHistory = debounce(fetchOrderedHistory, 300);

  useEffect(() => {
    debouncedFetchOrderedHistory();
  }, [customerId, selectedOrderStatus, selectedOrderDate]);

  const handleOrderStatusChange = (event) => {
    setSelectedOrderStatus(event.target.value);
  };

  const handleOrderDateChange = (event) => {
    setSelectedOrderDate(event.target.value);
    setDateRange(''); // Reset date range when a specific date is selected
  };


  // Handle click to open modal and fetch order items
  const handleOrderClick = (order) => {
    setSelectedOrder(order); // Set the selected order for the modal
    fetchOrderItems(order?.invoice_number); // Fetch order items
    document.body.style.overflow = 'hidden'; // Hide page scrollbar when modal opens
  };

  // Hide scrollbar when modal opens, restore when closed
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);
  
  useEffect(() => {
    const loadImages = async () => {
      const status = { ...imageStatus }; // Copy the current image status
      for (const product of orderItems) {
        const pictureCheck = await checkImage(product?.picture);
        const vectorPictureCheck = await checkImage(product?.vectorPicture);

        // Check if the picture URL works, if not check vectorPicture, if both fail use the no-image.jpg
        if (pictureCheck?.status === "ok") {
          status[product?.item_code] = pictureCheck?.path;
        } else if (vectorPictureCheck?.status === "ok") {
          status[product?.item_code] = vectorPictureCheck?.path;
        } else {
          status[product?.item_code] = `${pathObj?.s3ImagePath}/no-image.jpg`;
        }

        // Update image status for the current product as soon as it's ready
        setImageStatus((prevStatus) => ({ ...prevStatus, [product?.item_code]: status[product?.item_code] }));
      }
    };

    if (orderItems?.length > 0) {
      loadImages();
    }
  }, [orderItems]);


  // Modal component for displaying order items
  // const OrderItemsModal = () => (
  //   <div className={`modal ${isModalOpen ? 'show' : ''}`} tabIndex="-1" role="dialog" style={{ display: isModalOpen ? 'block' : 'none' }}>
  //     <div className="modal-dialog" role="document" style={{ maxWidth: '900px', width: '90%' }}>
  //       <div className="modal-content">
  //         <div className="modal-header" style={{ position: 'relative' }}>
  //           <h5 className="modal-title">Order Items for Invoice {selectedOrder?.invoice_number}</h5>
  //           <button
  //             type="button"
  //             className="close"
  //             aria-label="Close"
  //             onClick={() => {
  //               setIsModalOpen(false);
  //               setImageStatus({});
  //               setOrderItems([]);
  //               }}
  //               style={{
  //                 position: 'absolute',
  //                 top: '12px',
  //                 right: '16px',
  //                 background: 'none',
  //                 border: 'none',
  //                 fontSize: '1.5rem',
  //                 cursor: 'pointer',
  //                 lineHeight: 1,
  //                 color: '#333'
  //               }}
  //               >
  //               &times;
  //               </button>
  //             </div>
  //             <div className="modal-body" style={{ padding: 0 }}>
  //               {orderItems.length > 0 ? (
  //               <div className="order-items-responsive-table">
  //                 {/* Pagination logic */}
  //                 {(() => {
  //                 // Pagination state and logic
  //                 const [activePage, setActivePage] = React.useState(1);
  //                 const itemsCountPerPage = 5;
  //                 const indexOfLastItem = activePage * itemsCountPerPage;
  //                 const indexOfFirstItem = indexOfLastItem - itemsCountPerPage;
  //                 const currentItems = orderItems.slice(indexOfFirstItem, indexOfLastItem);

  //                 // Expose pagination state to modal scope
  //                 OrderItemsModal.activePage = activePage;
  //                 OrderItemsModal.setActivePage = setActivePage;
  //                 OrderItemsModal.itemsCountPerPage = itemsCountPerPage;
  //                 OrderItemsModal.currentItems = currentItems;

  //                 return null;
  //                 })()}
  //                 <table className="table d-none d-md-table">
  //                 <thead>
  //                   <tr>
  //                   <th style={{ width: '16.66%' }}>Image</th>
  //                   <th style={{ width: '16.66%' }}>Item Code</th>
  //                   <th style={{ width: '16.66%' }}>Item Name</th>
  //                   <th style={{ width: '16.66%' }}>Quantity</th>
  //                   <th style={{ width: '16.66%' }}>Price</th>
  //                   <th style={{ width: '16.66%' }}>Total</th>
  //                   </tr>
  //                 </thead>
  //                 <tbody>
  //                   {(OrderItemsModal.currentItems || orderItems).map((item, index) => (
  //                   <tr key={index}>
  //                     <td style={{ width: '16.66%' }}>
  //                     <a href="#">
  //                       <img
  //                       src={imageStatus[item?.item_code] || item?.picture || item?.vectorPicture || `${pathObj?.s3ImagePath}/no-image.jpg`}
  //                       onError={(e) => {
  //                         e.target.onerror = null;
  //                         e.target.src = `${pathObj?.s3ImagePath}/no-image.jpg`;
  //                       }}
  //                       alt={item.ItemName}
  //                       style={{ maxWidth: '60px', maxHeight: '60px', objectFit: 'contain' }}
  //                       />
  //                     </a>
  //                     </td>
  //                     <td style={{ width: '16.66%' }}>
  //                     <a
  //                       href="#"
  //                       style={{ color: '#007bff', textDecoration: 'underline', fontSize: '12px' }}
  //                       onClick={(e) => {
  //                       e.preventDefault();
  //                       setSelectedPicklist(item.piclist || []);
  //                       setIsPicklistModalOpen(true);
  //                       }}
  //                     >
  //                       {item.item_code}
  //                     </a>
  //                     </td>
  //                     <td style={{ width: '16.66%' }}>{item.product_name}</td>
  //                     <td style={{ width: '16.66%' }}>{item.quantity}</td>
  //                     <td style={{ width: '16.66%' }}>{currencySymbol}{item.price_per_unit}</td>
  //                     <td style={{ width: '16.66%' }}>{currencySymbol}{item.total_price}</td>
  //                   </tr>
  //                   ))}
  //                 </tbody>
  //                 </table>
  //                 {/* Responsive cards for mobile */}
  //                 <div className="d-block d-md-none">
  //                 {(OrderItemsModal.currentItems || orderItems).map((item, index) => (
  //                   <div key={index} className="order-item-card" style={{
  //                   border: '1px solid #eee',
  //                   borderRadius: '8px',
  //                   marginBottom: '12px',
  //                   padding: '12px',
  //                   display: 'flex',
  //                   alignItems: 'flex-start',
  //                   gap: '12px',
  //                   background: '#fff'
  //                   }}>
  //                   <div>
  //                     <img
  //                     src={imageStatus[item?.item_code] || item?.picture || item?.vectorPicture || `${pathObj?.s3ImagePath}/no-image.jpg`}
  //                     onError={(e) => {
  //                       e.target.onerror = null;
  //                       e.target.src = `${pathObj?.s3ImagePath}/no-image.jpg`;
  //                     }}
  //                     alt={item.ItemName}
  //                     style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '4px' }}
  //                     />
  //                   </div>
  //                   <div style={{ flex: 1 }}>
  //                     <div><strong>Item Code:</strong> {item.item_code}</div>
  //                     <div><strong>Item Name:</strong> {item.product_name}</div>
  //                     <div><strong>Quantity:</strong> {item.quantity}</div>
  //                     <div><strong>Price:</strong> {currencySymbol}{item.price_per_unit}</div>
  //                     <div><strong>Total:</strong> {currencySymbol}{item.total_price}</div>
  //                   </div>
  //                   </div>
  //                 ))}
  //                 </div>
  //                 {/* Pagination component */}
  //                 <div className="d-flex justify-content-center my-3">
  //                 <Pagination
  //                   activePage={OrderItemsModal.activePage || 1}
  //                   itemsCountPerPage={OrderItemsModal.itemsCountPerPage || 5}
  //                   totalItemsCount={orderItems.length}
  //                   pageRangeDisplayed={3}
  //                   onChange={OrderItemsModal.setActivePage}
  //                   itemClass="page-item"
  //                   linkClass="page-link"
  //                 />
  //                 </div>
  //               </div>
  //               ) : (
  //               <p>No items found for this order.</p>
  //               )}
  //             </div>
  //             <div className="modal-footer">
  //               <button
  //               type="button"
  //               className="button2"
  //               onClick={() => {
  //               setIsModalOpen(false);
  //               setImageStatus({});
  //               setOrderItems([]);
  //             }}
  //           >
  //             Close
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );
  const OrderItemsModal = () => {
  const [activePage, setActivePage] = React.useState(1);
  const itemsCountPerPage = 10;
  const indexOfLastItem = activePage * itemsCountPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsCountPerPage;
  const currentItems = orderItems.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to first page only when modal is opened or a new order is selected
  React.useEffect(() => {
    if (isModalOpen) {
      setActivePage(1);
    }
  }, [isModalOpen, selectedOrder]);

  const fadedStyle = isPicklistModalOpen
    ? {
        opacity: 0,
        pointerEvents: 'none',
        filter: 'blur(1px)',
        transition: 'opacity 0.2s, filter 0.2s'
      }
    : {};

  return (
    <div className={`modal ${isModalOpen ? 'show' : ''}`} tabIndex="-1" role="dialog" style={{
        display: isModalOpen ? 'block' : 'none',
        ...fadedStyle // Apply fade if PicklistModal is open
      }}
    >
      <div className="modal-dialog" role="document" style={{ maxWidth: '900px', width: '90%' }}>
        <div className="modal-content">
          <div className="modal-header" style={{ position: 'relative' }}>
            <h5 className="modal-title">Order Items for Invoice {selectedOrder?.invoice_number}</h5>
              <button
              type="button"
              className="close"
              aria-label="Close"
              onClick={() => {
                setIsModalOpen(false);
                setImageStatus({});
                setOrderItems([]);
              }}
              style={{
                position: 'absolute',
                top: '18px',
                right: '8px',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                lineHeight: 1,
                color: '#333'
              }}
            >
              &times;
            </button>
          </div>
          <div className="modal-body" style={{ padding: 0 }}>
            {orderItems.length > 0 ? (
              <div className="order-items-responsive-table">
                <table className="table d-none d-md-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Item Code</th>
                      <th>Item Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Total</th>
                      <th>Delivered Quantity</th>
                      <th>Remaining Open Quantity</th>
                      <th>Pick List ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <a href="#">
                            <img
                              src={imageStatus[item?.item_code] || item?.picture || item?.vectorPicture || `${pathObj?.s3ImagePath}/no-image.jpg`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${pathObj?.s3ImagePath}/no-image.jpg`;
                              }}
                              alt={item.ItemName}
                              style={{ maxWidth: '60px', maxHeight: '50px', objectFit: 'contain' }}
                            />
                          </a>
                        </td>
                        <td>{item.item_code}</td>
                          <td>{item.product_name}</td>
                          <td>{item.quantity}</td>
                          <td>{currencySymbol}{item.price_per_unit}</td>
                          <td>{currencySymbol}{item.total_price}</td>
                          <td>{item.quantity - item.RemainingOpenQuantity}</td>
                          <td>{item.RemainingOpenQuantity}</td>
                          <td>
                            <a
                              href="#"
                              style={{ color: '#007bff', textDecoration: 'underline', fontSize: '12px' }}
                              onClick={e => {
                                e.preventDefault();
                                fetchPicklistData(item.PickListIdNumber);
                                // fetchPicklistData(99);
                                setIsPicklistModalOpen(true);
                              }}
                            >
                            {item.PickListIdNumber}
                            </a>
                          </td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                      {/* Responsive cards for mobile */}
                <div className="d-block d-md-none">
                  {currentItems.map((item, index) => (
                    <div key={index} className="order-item-card" style={{
                      border: '1px solid #eee',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      background: '#fff'
                    }}>
                      <div>
                        <img
                          src={imageStatus[item?.item_code] || item?.picture || item?.vectorPicture || `${pathObj?.s3ImagePath}/no-image.jpg`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${pathObj?.s3ImagePath}/no-image.jpg`;
                          }}
                          alt={item.ItemName}
                          style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '4px' }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div><strong>Item Code:</strong> {item.item_code}</div>
                        <div><strong>Item Name:</strong> {item.product_name}</div>
                        <div><strong>Quantity:</strong> {item.quantity}</div>
                        <div><strong>Price:</strong> {currencySymbol}{item.price_per_unit}</div>
                        <div><strong>Total:</strong> {currencySymbol}{item.total_price}</div>
                        <div><strong>Delivered Quantity:</strong> {item.delivered_quantity}</div>
                        <div><strong>Remaining Open Quantity:</strong>{item.RemainingOpenQuantity}</div>
                        <div><strong>Pick List ID:</strong>{' '}
                            <a
                              href="#"
                              style={{ color: '#007bff', textDecoration: 'underline', fontSize: '12px' }}
                              onClick={e => {
                                e.preventDefault();
                                // setSelectedPicklist(item.piclist || []);
                                fetchPicklistData(item.PickListIdNumber);
                                setIsPicklistModalOpen(true);
                              }}
                            >
                              {item.PickListIdNumber}
                            </a>
                        </div>
                        {/* <div><strong>Order PDF:</strong> <a href={item.OrderPDF} target="_blank" rel="noopener noreferrer">View PDF</a></div> */}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination component */}
                <div className="d-flex justify-content-center my-3 pagination_box">
                  <Pagination
                    activePage={activePage}
                    itemsCountPerPage={itemsCountPerPage}
                    totalItemsCount={orderItems.length}
                    pageRangeDisplayed={3}
                    onChange={setActivePage}
                    itemClass="page-item"
                    linkClass="page-link"
                  />
                </div>
              </div>
            ) : (
              <p>No items found for this order.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

  const PicklistModal = () => (
    <div className="modal show" tabIndex="-1" role="dialog" style={{ display: 'block', zIndex: 1999 }}>
      <div className="modal-dialog" role="document" style={{ maxWidth: '900px', width: '90%' }}>
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center" style={{ position: 'relative' }}>
            <h5 className="modal-title mb-0">Picklist Details</h5>
            <a
              href="#"
              className="back-link"
              style={{ minWidth: 'unset', color: '#007bff', textDecoration: 'underline', fontSize: '16px' }}
              onClick={e => {
                e.preventDefault();
                setIsPicklistModalOpen(false);
                setSelectedPicklist([]);
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '1px' }} fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.25 14.25L6.75 9L11.25 3.75" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back
                </span>
              </a>
              </div>
              <div className="modal-body" style={{ padding: 0 }}>
              {selectedPicklist && selectedPicklist.length > 0 ? (
                <>
                {/* Desktop Table */}
                <div className="order-items-responsive-table">
                  <table className="table d-none d-md-table padding-0">
                    <thead>
                      <tr>
                        <th>S.No #</th>
                        <th>Order Entry</th>
                        <th>Item Code</th>
                        <th>Picked Qty</th>
                        <th>Pick Status</th>
                        <th>Released Qty</th>
                        {/* <th>Prev. Released Qty</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPicklist.map((pick, idx) => (
                        <tr key={pick.pick_list_line_id || idx}>
                          <td>{idx + 1}</td>
                          <td>{pick.order_entry}</td>
                          <td>{pick.item_code}</td>
                          <td>{pick.picked_quantity}</td>
                          <td>{pick.pick_status ? pick.pick_status.replace('ps_', '') : ''}</td>
                          <td>{pick.released_quantity}</td>
                          {/* <td>{pick.previously_released_quantity}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Cards */}
                <div className="d-block d-md-none">
                  {(selectedPicklist && selectedPicklist.length > 0) ? (
                    selectedPicklist.map((pick, idx) => (
                      <div key={pick.pick_list_line_id || idx} className="order-item-card mb-3 p-2" style={{ border: '1px solid #eee', borderRadius: '8px', background: '#fff' }}>
                        <div><strong>S.No #:</strong> {idx + 1}</div>
                        <div><strong>Order Entry:</strong> {pick.order_entry}</div>
                        <div><strong>Item Code:</strong> {pick.item_code}</div>
                        <div><strong>Picked Qty:</strong> {pick.picked_quantity}</div>
                        <div><strong>Pick Status:</strong> {pick.pick_status ? pick.pick_status.replace('ps_', '') : ''}</div>
                        <div><strong>Released Qty:</strong> {pick.released_quantity}</div>
                        {/* <div><strong>Prev. Released Qty:</strong> {pick.previously_released_quantity}</div> */}
                      </div>
                    ))
                  ) : (
                    <div className="text-center my-3">No picklist data available.</div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-center my-3">No picklist data available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const downloadPDF = async (orderId) => {
    try {
      // console.log('Downloading PDF for order ID:', orderId);
      const response = await fetch(`${pathObj.baseURL}/orders/${orderId}/download-pdf`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
        },
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'order-details.pdf';
      link.click();
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  return (
    <>
      <div className="cetogry_listing">
        <div className="container">
          <div className="clinner d-flex flex-wrap align-items-start">
            <MyAccountSideBar />

            <div className="common_right_block">
              <div className="contact_details position-relative">
                <h1>Open Orders</h1>

                {/* Filters (if applicable) */}
                <div className="ohFilter common_form">
                  <div className="date_filter d-flex align-items-center gap-2">
                    <input
                      type="date"
                      className="form-control"
                      onChange={handleOrderDateChange}
                      value={selectedOrderDate}
                      placeholder='dd/mm/yyyy'
                    />
                    <select
                      className="form-control form-select"
                      onChange={e => {
                        setDateRange(e.target.value);
                        if (e.target.value === "1m") {
                          const date = moment().subtract(1, "months").format("YYYY-MM-DD");
                          setSelectedOrderDate(date);
                        } else if (e.target.value === "3m") {
                          const date = moment().subtract(3, "months").format("YYYY-MM-DD");
                          setSelectedOrderDate(date);
                        } else if (e.target.value === "6m") {
                          const date = moment().subtract(6, "months").format("YYYY-MM-DD");
                          setSelectedOrderDate(date);
                        } else if (e.target.value === "1y") {
                          const date = moment().subtract(12, "months").format("YYYY-MM-DD");
                          setSelectedOrderDate(date);
                        } else {
                          setSelectedOrderDate("");
                        }
                      }}
                      value={
                        selectedOrderDate === moment().subtract(1, "months").format("YYYY-MM-DD")
                          ? "1m"
                          : selectedOrderDate === moment().subtract(3, "months").format("YYYY-MM-DD")
                          ? "3m"
                          : selectedOrderDate === moment().subtract(6, "months").format("YYYY-MM-DD")
                          ? "6m"
                          : selectedOrderDate === moment().subtract(12, "months").format("YYYY-MM-DD")
                          ? "1y"
                          : ""
                      }
                    >
                      <option value="">Date Range</option>
                      <option value="1m">Last 1 Month</option>
                      <option value="3m">Last 3 Months</option>
                      <option value="6m">Last 6 Months</option>
                      <option value="1y">Last 12 Months</option>
                    </select>
                  </div>
                  <div className="order_status">
                    <select className="form-control form-select" onChange={handleOrderStatusChange} value={selectedOrderStatus}>
                      <option value="">Order Status</option>
                      {Object.entries(orderedFilterData).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Order History Table */}
                <div className="pt-4 mt-md-3">
                  <div className="common_table table-responsive" style={{height:'500px'}}>
                    <table >
                      <thead style={{ position:'sticky', top: "-2px" , background:'#F4F5FF'}}>
                        <tr className="text-center">
                          <th>Order No.</th>
                          <th>Order Date</th>
                          <th>Order Delivery Date</th>
                          <th>Customer Reference</th>
                          <th>Payment Reference</th>
                          <th>Order Total</th>
                          <th>Order Total (INCVAT)</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      {loading ? (
                        <div className="loader productListLoader" style={{ marginTop: '30%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></div>
                      ) : (
                        <tbody>
                          {orderedData.length > 0 ? (
                            orderedData.map((order) => (
                              <tr key={order.order_id}  className="text-center">
                                <td onClick={() => handleOrderClick(order)} style={{ cursor: 'pointer', color: '#0e1fe3' }}>
                                  {order?.invoice_number}
                                </td>
                                <td style={{ width: '150px' }}>
                                  {moment(order?.order_date).format('DD MMM, YYYY')}
                                </td>
                                <td className="text-center" style={{ width: '150px' }}>
                                  {moment(order?.order_delivery_date).format('DD MMM, YYYY')}
                                </td>
                                <td className="text-center">{order.customer_reference}</td>
                                <td className="text-center">{order.payment_reference}</td>
                                <td className="text-center">{currencySymbol}{UpdatedDecimalDigits(order.order_total)}</td>
                                <td className="text-center">{currencySymbol}{UpdatedDecimalDigits(order.order_total_incVat)}</td>
                                <td className="text-center">{order.DocumentStatus ? order.DocumentStatus.replace('bost_', '') : ''}</td>
                                <td className="text-center">
                                  <button className="btn btn-primary btn-sm" onClick={() => downloadPDF(order.order_id)} title="Download PDF">
                                    <i className="fas fa-download"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center">
                                {errorText && <div className="error-message">{errorText}</div>}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items Modal */}
      {isModalOpen && (
        <div
          className="modal-backdrop show"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1040
          }}
        />
      )}
      {isModalOpen && <OrderItemsModal />}
      {isPicklistModalOpen && <PicklistModal />}
    </>
  );
};
export default OrderHistory;
